import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  Output,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { EoBaseFormControlComponent } from '../eo-base-form-control/eo-base-form-control.component';
import { EoFileUploadConfig } from '../eo-file-upload/eo-file-upload.model';

@Component({
  selector: 'eo-file-upload-base',
  templateUrl: './eo-file-upload-base.component.html',
  styleUrl: './eo-file-upload-base.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EoFileUploadBaseComponent),
      multi: true,
    },
  ],
})
export class EoFileUploadBaseComponent extends EoBaseFormControlComponent {
  protected override _config?: EoFileUploadConfig;
  override get config(): EoFileUploadConfig | undefined {
    return this._config;
  }
  override set config(val: EoFileUploadConfig | undefined) {
    this._config = val;

    if (this.config?.control.value) {
      this.files = this.config?.control.value;
    }
  }

  @Input() chooseLabel?: string;
  @Input() chooseIcon?: string;
  @Input() chooseStyleClass?: string;
  @Input() cancelLabel?: string;
  @Input() cancelIcon?: string;
  @Input() cancelStyleClass?: string;
  @Input() accept?: string;
  @Input() maxFileSize?: number;
  @Input() fileLimit?: number;
  @Input() invalidFileSizeMessageSummary?: string;
  @Input() invalidFileSizeMessageDetail?: string;
  @Input() invalidFileTypeMessageSummary?: string;
  @Input() invalidFileTypeMessageDetail?: string;
  @Input() invalidFileLimitMessageSummary?: string;
  @Input() invalidFileLimitMessageDetail?: string;
  @Input() multiple?: boolean;
  @Input() uploadedFiles?: any[];
  @Input() prototypeUrl?: string;

  @Output() select = new EventEmitter<File[]>();
  @Output() onSelect = new EventEmitter<File[]>();
  @Output() removePending = new EventEmitter<File>();
  @Output() clearPending = new EventEmitter<File[]>();
  @Output() removeUploaded = new EventEmitter<File>();
  @Output() clearUploaded = new EventEmitter<File[]>();

  override ngOnInit(): void {
    super.ngOnInit();
    this.subscriptions.push(
      this.formControl.valueChanges.subscribe((value) => {
        if (!value) {
          this.files = [];
        }
      })
    );
  }

  public files: File[] = [];

  get getChooseLabel(): string {
    return this.chooseLabel ?? this.config?.chooseLabel ?? 'Επιλογή Αρχείου';
  }

  get getChooseIcon(): string | undefined {
    return this.chooseIcon ?? this.config?.chooseIcon;
  }

  get getChooseStyleClass(): string | undefined {
    return this.chooseStyleClass ?? this.config?.chooseStyleClass;
  }

  get getCancelLabel(): string {
    return this.cancelLabel ?? this.config?.cancelLabel ?? 'Αφαίρεση';
  }

  get getCancelIcon(): string {
    return this.cancelIcon ?? this.config?.cancelIcon ?? 'pi pi-times';
  }

  get getRemoveStyleClass(): string | undefined {
    return this.config?.removeStyleClass;
  }

  get getCancelStyleClass(): string {
    return (
      this.cancelStyleClass ??
      this.config?.cancelStyleClass ??
      'p-button-outlined p-button-danger'
    );
  }

  get getAccept(): string {
    return (
      this.accept ??
      this.config?.accept ??
      '.pdf, .jpeg, .pjpeg, .jpg, .gif, .x-png, .png'
    );
  }

  get getMaxFileSize(): number | undefined {
    return this.maxFileSize ?? this.config?.maxFileSize;
  }

  get getFileLimit(): number | undefined {
    return this.fileLimit ?? this.config?.fileLimit;
  }

  get getInvalidFileSizeMessageSummary(): string | undefined {
    return (
      this.invalidFileSizeMessageSummary ??
      this.config?.invalidFileSizeMessageSummary
    );
  }

  get getInvalidFileSizeMessageDetail(): string | undefined {
    return (
      this.invalidFileSizeMessageDetail ??
      this.config?.invalidFileSizeMessageDetail
    );
  }

  get getInvalidFileTypeMessageSummary(): string | undefined {
    return (
      this.invalidFileTypeMessageSummary ??
      this.config?.invalidFileTypeMessageSummary
    );
  }

  get getInvalidFileTypeMessageDetail(): string | undefined {
    return (
      this.invalidFileTypeMessageDetail ??
      this.config?.invalidFileTypeMessageDetail
    );
  }

  get getInvalidFileLimitMessageSummary(): string {
    return (
      this.invalidFileLimitMessageSummary ??
      this.config?.invalidFileLimitMessageSummary ??
      'Έχετε υπερβεί τον μέγιστο αριθμό αρχείων.'
    );
  }

  get getInvalidFileLimitMessageDetail(): string {
    return (
      this.invalidFileLimitMessageDetail ??
      this.config?.invalidFileLimitMessageDetail ??
      ''
    );
  }

  get getMultiple(): boolean {
    return this.multiple === true || this.config?.multiple === true;
  }

  get getUploadedFiles(): any[] {
    return this.uploadedFiles ?? this.config?.uploadedFiles ?? [];
  }

  protected onFilesSelect($event: FileSelectEvent) {
    this.files = $event.currentFiles;
    this.formControl?.setValue(this.files);
    this.select.emit(this.files);
    this.onSelect.emit(this.files);
  }

  onDowlnoadPrototype(): void {
    window.open(this.prototypeUrl ?? this.config?.prototypeUrl ?? '');
  }

  protected showDownloadFile(): boolean {
    return (
      this.uploadedFiles?.length != undefined && this.uploadedFiles?.length > 0
    );
  }

  onFileRemove(event?: FileRemoveEvent | File) {
    if (!event) {
      this.onFilesClear();
      return;
    }

    const fileName =
      (event as FileRemoveEvent)?.file?.name ?? (event as File)?.name ?? '';

    this.files = this.files.filter((x) => x.name != fileName);
    this.formControl?.setValue(this.files);
  }

  onFilesClear() {
    this.files = [];
    this.formControl?.setValue(this.files);
  }

  hasFiles(): boolean {
    return this.files?.length > 0 || this.uploadedFiles?.length != 0;
  }

  get getDownloadPrototypeVisible(): boolean {
    return (
      this.prototypeUrl != undefined || this.config?.prototypeUrl != undefined
    );
  }
}
